import { createContext, useContext } from 'react';

import type { StorefrontFeatures } from '@/features/storefront/api/storefront';

/**
 * Which parts of this shop exist, for a component that does not know which
 * shop it is inside.
 *
 * Not every merchant is selling something. A clinic, a salon, a trainer wants
 * the catalogue, a page about each thing they do and a way to be contacted -
 * and a basket, a till and a sign-in link a shopper can never use are not
 * neutral furniture, they are three promises the business cannot keep. So the
 * shop's own answer travels down the tree and the chrome, the router and the
 * buy button all read it from here.
 *
 * Deliberately a store setting rather than part of the theme document: a theme
 * is a look, and a merchant who tries a new one must not find their cart back
 * on.
 */

/** The shop every storefront was before this setting existed. */
export const ALL_FEATURES_ON: StorefrontFeatures = {
  cart: true,
  checkout: true,
  accounts: true,
  search: true,
  blog: true,
  // Off: this is the shop as it was before the setting existed, and before it
  // existed no shop had a careers page.
  careers: false,
  enquiry: { label: 'Enquire now', target: '/pages/contact' },
  hover_image: null,
};

/**
 * All-on is the default for the same reason `StorefrontPathsContext` has one:
 * plenty of this code renders with no provider above it - the customiser's
 * header preview, a theme's static sample shop, every component test - and
 * each of those is a whole shop that simply has nothing to say about features.
 * Defaulting to "everything exists" is what let this setting arrive without
 * touching any of them.
 */
export const StorefrontFeaturesContext = createContext<StorefrontFeatures>(ALL_FEATURES_ON);

/** The features of the shop this component is being rendered inside. */
export function useStorefrontFeatures(): StorefrontFeatures {
  return useContext(StorefrontFeaturesContext);
}

/** The features that can hide a page. `enquiry` configures one, it is not one. */
export type FeatureKey = 'cart' | 'checkout' | 'accounts' | 'search' | 'blog' | 'careers';
